import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverRacesForEveryLevel() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-races-for-every-level";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Beginner-Friendly Races",
    "section-2": "Intermediate Races",
    "section-3": "Advanced Challenges",
    "section-4": "Charity & Community Runs",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouversunrun.com",
      label: "Vancouver Sun Run Official Website",
    },
    {
      url: "https://bmovanmarathon.ca",
      label: "BMO Vancouver Marathon",
    },
    {
      url: "https://www.destinationvancouver.com",
      label: "Tourism Vancouver",
    },
    {
      url: "https://vancouver.ca/doing-business/special-events.aspx",
      label: "City of Vancouver’s Special Events Page",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's "id" must match the ref you use in the corresponding <p ref={(el) => setSpotRef("id", el)} ...>
  // Coordinates are taken directly from the conversation details.
  const locations = [
    {
      id: "sun-run",
      lat: 49.28067,
      lng: -123.11849,
      popupText: "Vancouver Sun Run Start Area",
    },
    {
      id: "canada-day-run",
      lat: 49.3043,
      lng: -123.1443,
      popupText: "Canada Day Fun Run at Stanley Park Pavilion",
    },
    {
      id: "sea-wheeze",
      lat: 49.28878,
      lng: -123.11303,
      popupText: "Lululemon SeaWheeze Half Marathon (Convention Centre)",
    },
    {
      id: "eastside-10k",
      lat: 49.27605,
      lng: -123.0877,
      popupText: "Vancouver Eastside 10K near Strathcona Park",
    },
    {
      id: "bmo-marathon",
      lat: 49.2416,
      lng: -123.11123,
      popupText: "BMO Vancouver Marathon Start (Queen Elizabeth Park)",
    },
    {
      id: "knee-knackering-start",
      lat: 49.37523,
      lng: -123.27092,
      popupText: "Knee Knackering North Shore Trail Run Start (Horseshoe Bay)",
    },
    {
      id: "knee-knackering-end",
      lat: 49.32667,
      lng: -122.94928,
      popupText: "Knee Knackering Finish (Deep Cove)",
    },
    {
      id: "run-for-women",
      lat: 49.25783,
      lng: -123.21846,
      popupText: "Run for Women at Pacific Spirit Park",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Run Vancouver: Your Ultimate Guide to the Best Races for Every Level</title>
        <meta
          name="description"
          content="Discover the top running events in Vancouver, from beginner-friendly 5Ks to advanced marathons and scenic trail races."
        />
        <meta
          name="keywords"
          content="running, vancouver, marathons, races, half marathon, 5K, 10K"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-races-for-every-level.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Run Vancouver: Your Ultimate Guide to the Best Races for Every Level"
        />
        <meta
          property="og:description"
          content="A comprehensive guide to Vancouver's running events for runners of all experience levels."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-races-for-every-level.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-races-for-every-level.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Run Vancouver: Your Ultimate Guide to the Best Races for Every Level"
        />
        <meta
          name="twitter:description"
          content="Explore Vancouver's top races, from 5Ks to marathons, plus local tips and FAQs."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-races-for-every-level.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Scenic photo of runners in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a runner’s paradise, offering stunning ocean views,
              lush parks, and a vibrant cityscape—perfect for every skill level.
              Whether you’re lacing up for your first 5K or tackling a rigorous
              marathon, Vancouver’s diverse range of races provides something
              for everyone. Read on for an overview of the top events, along
              with practical tips to help you make the most of race day.
            </p>

            {/* SECTION: Beginner-Friendly Races */}
            <h2 id="section-1">Beginner-Friendly Races</h2>

            <h3>Vancouver Sun Run (10K)</h3>
            <p
              ref={(el) => setSpotRef("sun-run", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.28067}
                coordX={-123.11849}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;Starts near Georgia Street. One of Canada’s largest 10K
              events, known for its festive atmosphere and scenic downtown
              route. Expect a flat course, supportive spectators, and
              well-organized logistics. Early registration fees hover around CAD
              40. Be sure to arrive early or consider public transit to avoid
              traffic congestion.
            </p>

            <h3>Canada Day Fun Run (5K)</h3>
            <p
              ref={(el) => setSpotRef("canada-day-run", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.3043}
                coordX={-123.1443}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;Stanley Park Pavilion area. Celebrate Canada’s birthday with
              a low-pressure 5K that’s perfect for beginners and families. Post-
              race festivities often include live music and food stalls. Typical
              registration costs around CAD 25, with some group or family
              discounts available.
            </p>

            {/* SECTION: Intermediate Races */}
            <h2 id="section-2">Intermediate Races</h2>

            <h3>Lululemon SeaWheeze Half Marathon</h3>
            <p
              ref={(el) => setSpotRef("sea-wheeze", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.28878}
                coordX={-123.11303}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;Vancouver Convention Centre. A half marathon that
              beautifully highlights Vancouver’s scenic coastlines. Known for
              pre-race yoga sessions, live music along the route, and a lively
              post-race festival. Registration (~CAD 150–200) sells out quickly,
              so mark your calendar early.
            </p>

            <h3>Vancouver Eastside 10K</h3>
            <p
              ref={(el) => setSpotRef("eastside-10k", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.27605}
                coordX={-123.0877}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;Strathcona Park. A 10K run through Vancouver’s historic
              Eastside neighborhoods. Expect moderate inclines, energetic local
              support, and a post-race market. Registration typically ranges
              from CAD 45–60.
            </p>

            {/* SECTION: Advanced Challenges */}
            <h2 id="section-3">Advanced Challenges</h2>

            <h3>BMO Vancouver Marathon</h3>
            <p
              ref={(el) => setSpotRef("bmo-marathon", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2416}
                coordX={-123.11123}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;Queen Elizabeth Park start line. This 42.2K race is
              Vancouver’s signature marathon, also offering half-marathon and 8K
              options. The route includes rolling hills, scenic waterfront
              paths, and finishes downtown. Registration costs range between CAD
              120–160, and it’s a certified Boston Marathon qualifier.
            </p>

            <h3>Knee Knackering North Shore Trail Run (30 miles)</h3>
            <p
              ref={(el) => setSpotRef("knee-knackering-start", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Start Location:</strong>{" "}
              <Marker
                coordY={49.37523}
                coordX={-123.27092}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;Horseshoe Bay.
            </p>
            <p
              ref={(el) => setSpotRef("knee-knackering-end", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Finish Location:</strong>{" "}
              <Marker
                coordY={49.32667}
                coordX={-122.94928}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;Deep Cove. A 48K ultramarathon on the challenging North
              Shore trails. With roughly 8,000 feet of elevation gain, it’s
              considered one of the toughest local trail races. Expect rugged
              terrain, root-laden paths, and mandatory gear checks. Registration
              is usually around CAD 80–100.
            </p>

            {/* SECTION: Charity & Community Runs */}
            <h2 id="section-4">Charity & Community Runs</h2>

            <h3>Run for Women (5K or 10K)</h3>
            <p
              ref={(el) => setSpotRef("run-for-women", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.25783}
                coordX={-123.21846}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;Pacific Spirit Park. Supporting women’s mental health
              initiatives, this family-friendly run offers scenic forest trails
              and a supportive, inclusive environment. Distances often range
              from 5K to 10K. A great way to give back while you run!
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What is the best time of year to run a race in Vancouver?</h3>
              <p>
                Spring (April–June) and early fall (September–October) typically
                have milder temperatures and less rain, making them ideal for
                outdoor races.
              </p>

              <h3>2. Are there races suitable for walkers?</h3>
              <p>
                Absolutely. Many 5K and even some 10K events welcome walkers.
                Just check the race’s official site to confirm any time limits.
              </p>

              <h3>3. How can I find local training groups?</h3>
              <p>
                Vancouver boasts several running clubs, including the Running
                Room and the Vancouver Falcons Athletic Club. Lululemon’s run
                crews and community boards also host regular training sessions.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Many races require permits that may result in road closures, so
              always check official race websites or the City of Vancouver’s
              Special Events Page. Additionally, runners are encouraged to
              respect environmental guidelines, especially when racing on
              sensitive trails—pack out any waste, stick to marked routes, and
              consider bringing your own reusable water bottle or collapsible
              cup.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From festive 5Ks to high-adrenaline trail ultras, Vancouver offers
              a running event for everyone. Whether you’re chasing a personal
              best or just exploring the city on foot, these races showcase the
              best of the region’s scenic beauty and vibrant culture. Lace up,
              sign up, and enjoy running in one of Canada’s most spectacular
              cities!
            </p>
            <p>
              <em>
                Disclaimer: Always check official resources for the most
                up-to-date information, and consult a healthcare professional
                before beginning any new training regimen.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}